import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  X,
  Building2,
  MapPin,
  Mail,
  Phone,
  User,
  Wrench,
  Droplet,
  Clock,
  Edit3,
  Save,
  Loader2,
  FileText,
  History,
  ExternalLink
} from 'lucide-react';
import { cspireAPI, serviceCallsAPI } from '@shared/apiClient';
import { formatDate } from '@shared/utils';
import toast from 'react-hot-toast';
import CopyTextButton from './CopyTextButton';

const SITE_EQUIPMENT_FIELDS = [
  { key: 'generatorType', label: 'Type', group: 'generator' },
  { key: 'generatorModel', label: 'Model', group: 'generator' },
  { key: 'generatorSerialNumber', label: 'Serial Number', group: 'generator' },
  { key: 'generatorSize', label: 'Size', group: 'generator' },
  {
    key: 'generatorCoolingType',
    label: 'Cooling Type',
    group: 'generator',
    type: 'select',
    options: [
      { value: '', label: 'Unknown' },
      { value: 'air', label: 'Air-cooled' },
      { value: 'water', label: 'Water-cooled' }
    ]
  },
  { key: 'generatorExerciseTime', label: 'Exercise Time', group: 'generator' },
  { key: 'airFilter', label: 'Air Filter', group: 'filters' },
  { key: 'fuelFilter1', label: 'Fuel Filter 1', group: 'filters' },
  { key: 'fuelFilter2', label: 'Fuel Filter 2', group: 'filters' },
  { key: 'oilFilter1', label: 'Oil Filter 1', group: 'filters' },
  { key: 'oilFilter2', label: 'Oil Filter 2', group: 'filters' },
  { key: 'fuelAdditive', label: 'Fuel Additive', group: 'fluids' },
  { key: 'fuelType', label: 'Fuel Type', group: 'fluids' },
  { key: 'oilType', label: 'Oil Type', group: 'fluids' },
  { key: 'oilAmount', label: 'Oil Amount', group: 'fluids' },
  { key: 'batterySize', label: 'Size', group: 'battery' },
  { key: 'batteryDateCode', label: 'Date Code', group: 'battery' },
  { key: 'batteryDateChanged', label: 'Date Changed', group: 'battery', type: 'date' },
];

const StatusBadge = ({ status }) => {
  const configs = {
    assigned: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-purple-100 text-purple-800',
    completed: 'bg-green-100 text-green-800',
    return: 'bg-orange-100 text-orange-800',
    cancelled: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${configs[status] || 'bg-gray-100 text-gray-800'}`}>
      {status?.replace('_', ' ') || 'N/A'}
    </span>
  );
};

export default function CspireSiteDetailsModal({ site, workOrders, isOpen, onClose, onViewServiceCall }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('details');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && site) {
      setActiveTab('details');
      setIsEditing(false);
      const data = {};
      SITE_EQUIPMENT_FIELDS.forEach(({ key, type }) => {
        if (type === 'date') {
          data[key] = site[key] ? new Date(site[key]).toISOString().split('T')[0] : '';
        } else {
          data[key] = site[key] || '';
        }
      });
      setFormData(data);
    }
  }, [isOpen, site]);

  const updateMutation = useMutation(
    (payload) => cspireAPI.updateSite(site.id, payload),
    {
      onSuccess: () => {
        toast.success('Site equipment info updated!');
        queryClient.invalidateQueries('cspireSites');
        queryClient.invalidateQueries('cspireWorkOrders');
        setIsEditing(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update site');
      }
    }
  );

  if (!isOpen || !site) return null;

  const siteWorkOrders = (workOrders || []).slice().sort((a, b) => {
    const dateA = new Date(a.serviceCall?.completedDate || a.createdAt);
    const dateB = new Date(b.serviceCall?.completedDate || b.createdAt);
    return dateB - dateA;
  });

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {};
      SITE_EQUIPMENT_FIELDS.forEach(({ key }) => {
        payload[key] = formData[key]?.trim?.() || null;
      });
      await updateMutation.mutateAsync(payload);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    const data = {};
    SITE_EQUIPMENT_FIELDS.forEach(({ key, type }) => {
      if (type === 'date') {
        data[key] = site[key] ? new Date(site[key]).toISOString().split('T')[0] : '';
      } else {
        data[key] = site[key] || '';
      }
    });
    setFormData(data);
    setIsEditing(false);
  };

  const dash = <span className="text-gray-400">—</span>;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-indigo-50 px-4 py-5 sm:px-6 border-b border-indigo-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Building2 className="h-6 w-6 text-indigo-600" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {site.locationName || 'CSpire Site'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Site ID: {site.siteId || 'N/A'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('details')}
                className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'details'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Details
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'history'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Service History ({siteWorkOrders.length})
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="px-4 py-5 sm:p-6 max-h-[70vh] overflow-y-auto">
            {activeTab === 'details' ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Left Column: Location & Contact */}
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <MapPin className="h-4 w-4 mr-2 text-indigo-600" />
                      Location
                    </h4>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Address</dt>
                        <dd className="mt-0.5 text-sm text-gray-900">{site.addressLine1 || dash}</dd>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <dt className="text-xs font-medium text-gray-500">City</dt>
                          <dd className="mt-0.5 text-sm text-gray-900">{site.city || dash}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-500">State</dt>
                          <dd className="mt-0.5 text-sm text-gray-900">{site.state || dash}</dd>
                        </div>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Zip Code</dt>
                        <dd className="mt-0.5 text-sm text-gray-900">{site.zipCode || dash}</dd>
                      </div>
                      {(site.latitude || site.longitude) && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Coordinates</dt>
                          <dd className="mt-0.5 text-sm text-gray-900">
                            {site.latitude}, {site.longitude}
                            {site.latitude && site.longitude && (
                              <a
                                href={`https://www.google.com/maps?q=${site.latitude},${site.longitude}`}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-2 text-indigo-600 hover:text-indigo-800"
                              >
                                <ExternalLink className="inline h-3.5 w-3.5" />
                              </a>
                            )}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <User className="h-4 w-4 mr-2 text-indigo-600" />
                      Contact
                    </h4>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Name</dt>
                        <dd className="mt-0.5 text-sm text-gray-900">{site.contactName || dash}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Email</dt>
                        <dd className="mt-0.5 text-sm text-gray-900">
                          {site.contactEmail ? (
                            <a href={`mailto:${site.contactEmail}`} className="text-indigo-600 hover:underline">{site.contactEmail}</a>
                          ) : dash}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Phone</dt>
                        <dd className="mt-0.5 text-sm text-gray-900">
                          {site.contactPhone ? (
                            <a href={`tel:${site.contactPhone}`} className="text-indigo-600 hover:underline">{site.contactPhone}</a>
                          ) : dash}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {site.notes && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                        <FileText className="h-4 w-4 mr-2 text-gray-600" />
                        Notes
                      </h4>
                      <p className="text-xs text-gray-700 whitespace-pre-wrap">{site.notes}</p>
                    </div>
                  )}

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">System Info</h4>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Status</dt>
                        <dd className="mt-0.5">
                          {site.isActive !== false ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Inactive</span>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Created</dt>
                        <dd className="mt-0.5 text-xs text-gray-900">{formatDate(site.createdAt)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Last Updated</dt>
                        <dd className="mt-0.5 text-xs text-gray-900">{formatDate(site.updatedAt)}</dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {/* Middle Column: Generator & Filters */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-900">Equipment Details</span>
                    <button
                      type="button"
                      onClick={() => isEditing ? handleCancelEdit() : setIsEditing(true)}
                      className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                    >
                      <Edit3 className="h-3 w-3" />
                      {isEditing ? 'Cancel' : 'Edit'}
                    </button>
                  </div>

                  {/* Generator */}
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <Wrench className="h-4 w-4 mr-2 text-blue-600" />
                      Generator Details
                    </h4>
                    <dl className="space-y-2">
                      {SITE_EQUIPMENT_FIELDS.filter(f => f.group === 'generator').map(({ key, label, type, options }) => (
                        <div key={key}>
                          <dt className="text-xs font-medium text-gray-600">{label}</dt>
                          {isEditing ? (
                            type === 'select' ? (
                              <select
                                value={formData[key] || ''}
                                onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                                className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                              >
                                {options.map((opt) => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type={type === 'date' ? 'date' : 'text'}
                                value={formData[key] || ''}
                                onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                                className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            )
                          ) : (
                            <dd className="mt-0.5 text-sm text-gray-900">
                              {key === 'generatorSerialNumber' ? (
                                <span className="font-mono">{site[key] || dash}</span>
                              ) : type === 'select' ? (
                                options.find((opt) => opt.value === site[key])?.label || dash
                              ) : (
                                site[key] || dash
                              )}
                            </dd>
                          )}
                        </div>
                      ))}
                    </dl>
                  </div>

                  {/* Battery */}
                  <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <Wrench className="h-4 w-4 mr-2 text-green-600" />
                      Battery Details
                    </h4>
                    <dl className="space-y-2">
                      {SITE_EQUIPMENT_FIELDS.filter(f => f.group === 'battery').map(({ key, label, type }) => (
                        <div key={key}>
                          <dt className="text-xs font-medium text-gray-600">{label}</dt>
                          {isEditing ? (
                            <input
                              type={type === 'date' ? 'date' : 'text'}
                              value={formData[key] || ''}
                              onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                              className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          ) : (
                            <dd className="mt-0.5 text-sm text-gray-900">
                              {type === 'date' && site[key] ? formatDate(site[key]) : site[key] || dash}
                            </dd>
                          )}
                        </div>
                      ))}
                    </dl>
                  </div>

                  {isEditing && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={isSaving}
                        className="btn btn-primary flex items-center gap-1.5 text-sm"
                      >
                        {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Save Equipment Info
                      </button>
                    </div>
                  )}
                </div>

                {/* Right Column: Filters & Fluids */}
                <div className="space-y-4">
                  {/* Filters & Fluids */}
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <Droplet className="h-4 w-4 mr-2 text-amber-600" />
                      Filters & Fluids
                    </h4>
                    <dl className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        {SITE_EQUIPMENT_FIELDS.filter(f => f.group === 'filters').map(({ key, label, type }) => (
                          <div key={key}>
                            <dt className="text-xs font-medium text-gray-600">{label}</dt>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData[key] || ''}
                                onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                                className="w-full mt-0.5 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            ) : (
                              <dd className="mt-0.5 text-xs text-gray-900">{site[key] || dash}</dd>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="pt-2 border-t border-amber-300">
                        <div className="grid grid-cols-2 gap-2">
                          {SITE_EQUIPMENT_FIELDS.filter(f => f.group === 'fluids').map(({ key, label, type }) => (
                            <div key={key}>
                              <dt className="text-xs font-medium text-gray-600">{label}</dt>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={formData[key] || ''}
                                  onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                                  className="w-full mt-0.5 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                                />
                              ) : (
                                <dd className="mt-0.5 text-xs text-gray-900">{site[key] || dash}</dd>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            ) : (
              /* Service History Tab */
              <div>
                {siteWorkOrders.length === 0 ? (
                  <div className="text-center py-12">
                    <Clock className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No Service History</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      This site doesn't have any work orders yet.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {siteWorkOrders.map((order) => {
                      const sc = order.serviceCall;
                      const visits = Array.isArray(sc?.visits) ? sc.visits : [];

                      return (
                        <div key={order.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-indigo-900">WO# {order.workOrderNumber}</span>
                              {sc && <StatusBadge status={sc.status} />}
                              {sc?.isFinalized && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                  Finalized
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {sc?.completedDate && (
                                <span className="text-xs text-gray-500">
                                  Completed: {formatDate(sc.completedDate)}
                                </span>
                              )}
                              {!sc && order.createdAt && (
                                <span className="text-xs text-gray-500">
                                  Created: {formatDate(order.createdAt)}
                                </span>
                              )}
                              {sc && (
                                <button
                                  type="button"
                                  onClick={() => onViewServiceCall?.(sc)}
                                  className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                                >
                                  View Service Call
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Work order meta */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-600">
                            {order.subject && (
                              <div><span className="font-medium">Subject:</span> {order.subject}</div>
                            )}
                            {order.severity && (
                              <div><span className="font-medium">Severity:</span> {order.severity}</div>
                            )}
                            {order.reason && (
                              <div><span className="font-medium">Reason:</span> {order.reason}</div>
                            )}
                            {order.dueDate && (
                              <div><span className="font-medium">Due:</span> {formatDate(order.dueDate)}</div>
                            )}
                          </div>

                          {/* Technician */}
                          {sc?.technician && (
                            <div className="text-xs text-gray-600">
                              <span className="font-medium">Technician:</span> {sc.technician.firstName} {sc.technician.lastName}
                            </div>
                          )}

                          {/* Work Performed / Description */}
                          {(sc?.workPerformed || order.description) && (
                            <div>
                              <div className="mb-1 flex items-center justify-between gap-2">
                                <span className="text-xs font-medium text-gray-700">
                                  {sc?.workPerformed ? 'Work Performed' : 'Description'}
                                </span>
                                <CopyTextButton
                                  text={sc?.workPerformed || order.description || ''}
                                  size="xs"
                                />
                              </div>
                              <div className="bg-gray-50 rounded p-2 text-sm text-gray-700 whitespace-pre-wrap">
                                {sc?.workPerformed || order.description}
                              </div>
                            </div>
                          )}

                          {/* Visits */}
                          {visits.length > 0 && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <History className="h-4 w-4 text-yellow-700" />
                                <h4 className="text-sm font-semibold text-gray-900">
                                  Visit History ({visits.length} visit{visits.length > 1 ? 's' : ''})
                                </h4>
                              </div>
                              <div className="space-y-2">
                                {visits.map((visit) => (
                                  <div key={visit.id} className="bg-white rounded-md p-2 border border-yellow-100 text-sm">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-medium text-gray-900">Visit #{visit.visitNumber}</span>
                                      <span className="text-xs text-gray-600">{formatDate(visit.visitDate)}</span>
                                    </div>
                                    {visit.workPerformed && (
                                      <div>
                                        <div className="mb-0.5 flex items-center justify-between gap-1">
                                          <span className="text-xs font-medium text-gray-600">Work Performed:</span>
                                          <CopyTextButton text={visit.workPerformed} size="xs" />
                                        </div>
                                        <p className="text-gray-700 text-xs whitespace-pre-wrap">{visit.workPerformed}</p>
                                      </div>
                                    )}
                                    {(visit.generatorHours || visit.fuelLevel) && (
                                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-600">
                                        {visit.generatorHours && (
                                          <div>
                                            <span className="font-medium">Gen Hours:</span> {parseFloat(visit.generatorHours).toFixed(2)}
                                          </div>
                                        )}
                                        {visit.fuelLevel && (
                                          <div>
                                            <span className="font-medium">Fuel Level:</span> {visit.fuelLevel}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Schedule / Invoice */}
                          {(order.schedule || order.invoiceInstructions) && (
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              {order.schedule && (
                                <span>Schedule: {order.schedule.name}</span>
                              )}
                              {order.invoiceInstructions && (
                                <span className="truncate max-w-xs" title={order.invoiceInstructions}>
                                  Invoice: {order.invoiceInstructions.substring(0, 60)}...
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
